import clsx from "clsx";
import { flatMap, snakeCase } from "lodash";
import PropTypes from "prop-types";
import React, { isValidElement } from "react";
import Col from "react-bootstrap/Col";
import Feedback from "react-bootstrap/Feedback";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";

import { field } from "../../forms";
import { RenderWhen, ConditionalTooltip } from "../../components";

const Widget = function Widget(props) {
  const {
    id,
    label,
    labelBold,
    help,
    helpImportant,
    errors = [],
    defaultValue,
    skipColon,
    vertical = false,
    marginBottom = 25,
    tooltip = {},
    required = false,
    showRequiredMark = false,
    autoComplete,
    as = "input",
    type = "",
    ...extra
  } = props;

  let filteredErrors;
  if (typeof errors === "object" && errors !== null) {
    // turn numeric keyed object into a list
    filteredErrors = flatMap(errors, value => value);
  } else {
    filteredErrors = errors;
  }
  const hasErrors = filteredErrors?.length;

  const widgetClass = clsx(hasErrors && "has-error", props.className, {
    hidden: props.hidden
  });
  const complexLabel = isValidElement(label);
  const labelContainerClassName = clsx({ "mb-5": vertical });

  return (
    <Form.Group
      as={vertical ? "div" : Row}
      className={`${widgetClass} mb-${marginBottom}`}
      controlId={id || props.name}
    >
      <Col xs={vertical ? 12 : 3} className={labelContainerClassName}>
        <RenderWhen condition={complexLabel}>{label}</RenderWhen>
        <RenderWhen condition={!complexLabel && label}>
          <Form.Label
            className={clsx(
              labelBold && "form-label--bold",
              showRequiredMark && required && "show-required-mark"
            )}
          >
            {skipColon ? `${label}` : `${label}:`}
          </Form.Label>
        </RenderWhen>
      </Col>
      <Col xs={vertical ? 12 : 9}>
        <ConditionalTooltip
          condition={tooltip.show}
          tooltip={tooltip.message ?? ""}
          className={tooltip?.className ?? ""}
          wide={tooltip?.wide}
          placement="bottom-start"
        >
          <Form.Control
            defaultValue={defaultValue}
            required={required}
            {...extra}
            as={as}
            type={type}
            isInvalid={hasErrors}
            data-testid={snakeCase(id || props.name)}
            autoComplete={autoComplete}
          />
        </ConditionalTooltip>
        <Feedback type="invalid">
          {filteredErrors.map((error, key) => (
            <div key={key}>{error}</div>
          ))}
        </Feedback>
        {help && <Form.Text>{help}</Form.Text>}
        {helpImportant && (
          <Form.Text className="form-text--important">
            {helpImportant}
          </Form.Text>
        )}
      </Col>
    </Form.Group>
  );
};

Widget.propTypes = {
  id: PropTypes.string,
  autoFocus: PropTypes.bool,
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.object]),
  name: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.node
  ]),
  labelBold: PropTypes.bool,
  defaultValue: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string
      })
    )
  ]),
  colCtrlLbl: PropTypes.number,
  colCtrl: PropTypes.number,
  placeholder: PropTypes.string,
  help: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  helpImportant: PropTypes.string,
  className: PropTypes.string,
  hidden: PropTypes.bool,
  errors: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  onChange: PropTypes.func.isRequired,
  skipColon: PropTypes.bool,
  vertical: PropTypes.bool,
  marginBottom: PropTypes.oneOf([0, 5, 10, 15, 20, 25, 30]),
  rows: PropTypes.number,
  disabled: PropTypes.bool,
  value: PropTypes.string,
  tooltip: PropTypes.shape({
    message: PropTypes.string,
    show: PropTypes.bool,
    className: PropTypes.string,
    wide: PropTypes.bool
  }),
  required: PropTypes.bool,
  showRequiredMark: PropTypes.bool,
  autoComplete: PropTypes.string,
  type: PropTypes.string
};

const FieldWidget = field(Widget);

export { Widget as default, FieldWidget };
